/**
 * Evaluation clip encode targets — match iOS `EvaluationVideoQuality`
 * (H.264, 720p, ~60 fps, Low ≈ 8 MB/min / 900 kbps).
 */

export const EVAL_VIDEO_CAPTURE = Object.freeze({
  width: 1280,
  height: 720,
  fps: 60,
});

/** Web evaluation capture only supports 30 / 60 (default 60). Native iOS can still use 120. */
export const WEB_EVAL_FPS_OPTIONS = Object.freeze([30, 60]);
export const DEFAULT_WEB_EVAL_FPS = 60;
/** Worst-case floor when 60fps is unavailable (typical front-camera limit). */
export const MIN_WEB_EVAL_FPS = 30;

/** Prefer 60, then 30 — never settle for ~15fps soft defaults. */
export function evalCaptureFpsFallbackLadder(fps = DEFAULT_WEB_EVAL_FPS) {
  const preferred = normalizeWebEvalFps(fps, DEFAULT_WEB_EVAL_FPS);
  if (preferred === DEFAULT_WEB_EVAL_FPS) {
    return [DEFAULT_WEB_EVAL_FPS, MIN_WEB_EVAL_FPS];
  }
  return [preferred];
}

/** Target average video bitrate (H.264), same as iOS `targetVideoBitRate`. */
export const EVAL_VIDEO_BITRATES = Object.freeze({
  low: 900_000,
  medium: 2_200_000,
  high: 4_500_000,
});

export const EVAL_VIDEO_AUDIO_BITRATE = 64_000;
export const EVAL_FIVE_MINUTE_SECONDS = 5 * 60;
export const EVAL_MAX_UPLOAD_BYTES_FIVE_MINUTES = 100 * 1024 * 1024;
export const EVAL_ENCODER_OVERSHOOT = 1.5;

export function estimatedEvalFileBytes(durationSec, quality = "low") {
  const seconds = Math.max(0, Number(durationSec) || 0);
  const total = evalVideoBitRate(quality) + EVAL_VIDEO_AUDIO_BITRATE;
  return Math.ceil((total / 8) * seconds);
}

const H264_MIME_CANDIDATES = Object.freeze([
  'video/mp4;codecs="avc1.640029,mp4a.40.2"',
  "video/mp4;codecs=avc1.640029,mp4a.40.2",
  'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4;codecs=avc1.640029",
  "video/mp4;codecs=avc1.42E01E",
  "video/mp4",
  "video/webm;codecs=avc1.42E01E,opus",
  "video/webm;codecs=avc1.42E01E",
  "video/webm;codecs=avc1",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
]);

export function normalizeEvalVideoQuality(raw, fallback = "low") {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (s === "low" || s === "medium" || s === "high") return s;
  return fallback;
}

export function evalVideoBitRate(quality) {
  const q = normalizeEvalVideoQuality(quality);
  return EVAL_VIDEO_BITRATES[q] || EVAL_VIDEO_BITRATES.low;
}

function recorderSupportsType(type, isTypeSupported) {
  if (!type) return false;
  try {
    return Boolean(isTypeSupported(type));
  } catch {
    return false;
  }
}

/** True when the mime string names an audio codec (or a bare container that will mux one). */
export function evalMimeTypeIncludesAudio(type) {
  const s = String(type || "")
    .trim()
    .toLowerCase();
  if (!s) return false;
  if (s.includes("mp4a") || s.includes("opus") || s.includes("pcm")) return true;
  // Container-only types usually pick an audio codec when a mic track is present.
  return s === "video/mp4" || s === "video/webm" || s === "video/quicktime";
}

/**
 * Prefer H.264 in MP4 (Safari + modern Chrome), same family as iOS uploads.
 * @param {(type: string) => boolean} [isTypeSupported]
 * @param {{ preferAudio?: boolean }} [options] when true, skip video-only codecs
 *   while an audio-capable candidate is available (instruction / voice clips).
 * @returns {string|undefined}
 */
export function pickEvalVideoMimeType(isTypeSupported, { preferAudio = false } = {}) {
  const probe =
    typeof isTypeSupported === "function"
      ? isTypeSupported
      : typeof MediaRecorder !== "undefined" &&
          typeof MediaRecorder.isTypeSupported === "function"
        ? (type) => MediaRecorder.isTypeSupported(type)
        : null;
  if (!probe) return undefined;
  if (!preferAudio) {
    for (const type of H264_MIME_CANDIDATES) {
      if (recorderSupportsType(type, probe)) return type;
    }
    return undefined;
  }
  let fallback;
  for (const type of H264_MIME_CANDIDATES) {
    if (!recorderSupportsType(type, probe)) continue;
    if (evalMimeTypeIncludesAudio(type)) return type;
    if (!fallback) fallback = type;
  }
  return fallback;
}

export function evalVideoExtension(mimeType) {
  const s = String(mimeType || "").toLowerCase();
  if (s.includes("webm")) return "webm";
  if (s.includes("quicktime") || s.includes(".mov")) return "mov";
  if (s.includes("mp4") || s.includes("avc1")) return "mp4";
  return "mp4";
}

/** Strip codec parameters so uploads match server MIME allow-lists. */
export function canonicalEvalVideoMime(mimeType) {
  const raw = String(mimeType || "").toLowerCase();
  const base = raw.split(";")[0].trim();
  if (base.includes("webm") || raw.includes("vp8") || raw.includes("vp9")) {
    return "video/webm";
  }
  if (base.includes("quicktime")) return "video/quicktime";
  if (
    base.includes("mp4") ||
    raw.includes("avc1") ||
    base === "video/mp4" ||
    !base
  ) {
    return "video/mp4";
  }
  if (base.startsWith("video/")) return base;
  return "video/mp4";
}

export function blobWithCanonicalEvalMime(blob) {
  if (!blob) return blob;
  const mime = canonicalEvalVideoMime(blob.type);
  if (String(blob.type || "").toLowerCase() === mime) return blob;
  return new Blob([blob], { type: mime });
}

export function normalizeWebEvalFps(raw, fallback = DEFAULT_WEB_EVAL_FPS) {
  const n = Number(raw);
  if (n === 30 || n === 60) return n;
  return fallback;
}

export function normalizeWebEvalFacingMode(raw, fallback = "environment") {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (s === "user" || s === "front") return "user";
  if (s === "environment" || s === "back" || s === "rear") return "environment";
  return fallback;
}

// Device labels follow the phone language (iOS localizes AVCaptureDevice names),
// so each pattern covers the app locales: en, fr, es, pt, de, nl, el.
const FRONT_LABEL_RE =
  /front|user|facetime|selfie|truedepth|avant|voorzijde|voorkant|μπροστ/;
const BACK_LABEL_RE =
  /back|rear|environment|arri[eè]re|trasera|traseira|r[uü]ck|achter|πίσω|πισω/;
const ULTRA_WIDE_LABEL_RE =
  /ultra\s*-?\s*wide|ultrawide|\buw\b|0\s*[.,]?\s*5\s*x?\b|ultra[\s-]*grand[\s-]*angle|ultra[\s-]*gran[\s-]*angular|ultra[\s-]*angular|ultra[\s-]*weitwinkel|ultra[\s-]*groothoek|υπερευρυγ/;
const WIDE_LABEL_RE =
  /\bwide\b|wide\s*-?\s*angle|grand[\s-]*angle|gran[\s-]*angular|grande[\s-]*angular|weitwinkel|groothoek|ευρυγ/;
const TELE_LABEL_RE =
  /\btele\b|telephoto|t[ée]l[ée]\s*-?\s*objecti|teleobjetiv|teleobjektiv|telelens|τηλεφακ|\b[2-9]\s*x\b/;
/** iPhone virtual cameras that switch between physical lenses on their own. */
const MULTI_LENS_LABEL_RE =
  /dual|triple|double|doble|dupla|tripla|dubbel|drievoud|dreifach|fusion|quadra|multi|continuum|virtual|διπλ|τριπλ/;

export function inferVideoInputFacing(label) {
  const s = String(label || "").toLowerCase();
  if (FRONT_LABEL_RE.test(s)) return "user";
  if (
    BACK_LABEL_RE.test(s) ||
    ULTRA_WIDE_LABEL_RE.test(s) ||
    WIDE_LABEL_RE.test(s) ||
    TELE_LABEL_RE.test(s)
  ) {
    return "environment";
  }
  return "unknown";
}

/** Prefer InputDeviceInfo.getCapabilities().facingMode when the browser exposes it. */
export function readVideoInputFacingMode(device) {
  try {
    const caps =
      typeof device?.getCapabilities === "function"
        ? device.getCapabilities()
        : null;
    const modes = caps?.facingMode;
    if (Array.isArray(modes)) {
      if (modes.includes("user")) return "user";
      if (modes.includes("environment")) return "environment";
    } else if (typeof modes === "string") {
      return normalizeWebEvalFacingMode(modes, "unknown");
    }
  } catch {
    /* ignore */
  }
  return inferVideoInputFacing(device?.label);
}

/**
 * Lens kind for a videoinput label. Wide / ultra-wide are detected before the
 * front/back defaults so front wide cameras stay selectable as 0.5×.
 */
export function inferVideoInputLens(label, facing = "unknown") {
  const s = String(label || "").toLowerCase();
  // Dual / triple arrays are virtual cameras, not a lens of their own.
  if (MULTI_LENS_LABEL_RE.test(s)) return "multi";
  // Ultra-wide / 0.5× (front or back) — before the front catch-all.
  if (ULTRA_WIDE_LABEL_RE.test(s) || WIDE_LABEL_RE.test(s)) return "ultrawide";
  if (TELE_LABEL_RE.test(s)) return "tele";
  if (facing === "user" || FRONT_LABEL_RE.test(s)) return "front";
  if (facing === "environment" || BACK_LABEL_RE.test(s)) return "back";
  return "unknown";
}

export function classifyVideoInputDevice(device, index = 0) {
  const label = String(device?.label || "");
  const facingRaw = readVideoInputFacingMode(device);
  const facing =
    facingRaw === "user" || facingRaw === "environment"
      ? facingRaw
      : inferVideoInputFacing(label);
  return {
    deviceId: String(device?.deviceId || ""),
    label,
    facing,
    lens: inferVideoInputLens(label, facing),
    index,
  };
}

/**
 * When the browser exposes several cameras on the same side without ultra/tele
 * labels (common on Android), treat the non-primary ones as wide so setup can
 * show a 0.5× chip.
 */
export function assignImplicitWideLenses(options) {
  const list = Array.isArray(options) ? options.map((o) => ({ ...o })) : [];
  const refineSide = (facingMode, mainLens) => {
    const side = list
      .filter((o) => o.facingMode === facingMode && o.lens !== "multi")
      .sort((a, b) => a.index - b.index);
    if (side.length < 2) return;
    const hasNamedWideOrTele = side.some(
      (o) => o.lens === "ultrawide" || o.lens === "tele"
    );
    if (hasNamedWideOrTele) return;
    // Keep the first as the main lens; mark the rest as wide.
    for (let i = 0; i < side.length; i += 1) {
      const opt = side[i];
      if (i === 0) {
        if (opt.lens === "unknown") opt.lens = mainLens;
        continue;
      }
      if (opt.lens === mainLens || opt.lens === "unknown") {
        opt.lens = "ultrawide";
      }
    }
  };
  refineSide("environment", "back");
  refineSide("user", "front");
  return list;
}

/** Dual/Triple Wide virtual rear cameras that can reach the ultra-wide lens. */
export function isWideArrayCameraLabel(label) {
  const s = String(label || "").toLowerCase();
  if (!MULTI_LENS_LABEL_RE.test(s)) return false;
  return (
    ULTRA_WIDE_LABEL_RE.test(s) ||
    WIDE_LABEL_RE.test(s) ||
    /dual\s*wide|triple\s*(wide|camera)|double\s*wide/.test(s)
  );
}

/**
 * iPhone often lists Front Ultra Wide as its own camera while the rear ultra
 * stays behind "Back Dual Wide Camera". Turn that virtual array into Normal
 * and/or Wide chips so calibration can propose the back wide lens too.
 */
export function promoteVirtualRearCameras(options) {
  const list = Array.isArray(options) ? options.map((o) => ({ ...o })) : [];
  const rearMulti = (o) =>
    o.facingMode === "environment" && o.lens === "multi";
  const hasBack = () =>
    list.some((o) => o.facingMode === "environment" && o.lens === "back");
  const hasUltra = () =>
    list.some((o) => o.facingMode === "environment" && o.lens === "ultrawide");

  if (!hasBack()) {
    const pick =
      list.find((o) => rearMulti(o) && isWideArrayCameraLabel(o.label)) ||
      list.find((o) => rearMulti(o));
    if (pick) pick.lens = "back";
  }

  if (hasBack() && !hasUltra()) {
    const pick = list.find(
      (o) => rearMulti(o) && isWideArrayCameraLabel(o.label)
    );
    if (pick) {
      pick.lens = "ultrawide";
      // Dual Wide defaults to 1×; Wide should open the ultra end of the array.
      if (!(pick.zoom > 0 && pick.zoom < 1)) pick.zoom = 0.5;
    }
  }

  return list;
}

/**
 * Cameras shown on the web calibration overlay. Falls back to Front / Back
 * facingMode when the browser has not enumerated labeled video inputs yet.
 * Virtual multi-lens cameras are hidden once the physical back lens is listed,
 * except `keepDeviceId` (the lens in use) so the active chip stays visible.
 */
export function buildWebEvalCameraOptions(devices, { keepDeviceId = "" } = {}) {
  const list = (Array.isArray(devices) ? devices : []).filter(
    (d) => d && (d.kind == null || d.kind === "videoinput")
  );
  if (!list.length) {
    return [
      {
        id: "facing:environment",
        deviceId: "",
        facingMode: "environment",
        lens: "back",
        label: "",
        index: 0,
      },
      {
        id: "facing:user",
        deviceId: "",
        facingMode: "user",
        lens: "front",
        label: "",
        index: 1,
      },
    ];
  }
  const mapped = list.map((device, index) => {
    const classified = classifyVideoInputDevice(device, index);
    const facingMode =
      classified.facing === "user"
        ? "user"
        : classified.facing === "environment"
          ? "environment"
          : "";
    return {
      id: classified.deviceId
        ? `device:${classified.deviceId}`
        : `idx:${index}`,
      deviceId: classified.deviceId,
      facingMode,
      lens: classified.lens,
      label: classified.label,
      index,
    };
  });
  const refined = promoteVirtualRearCameras(assignImplicitWideLenses(mapped));
  const hasPhysicalBack = refined.some((o) => o.lens === "back");
  const keepId = String(keepDeviceId || "");
  return refined
    .filter(
      (o) =>
        o.lens !== "multi" ||
        !hasPhysicalBack ||
        (keepId && o.deviceId === keepId)
    )
    .sort((a, b) => {
      // Back lenses, then front lenses; within a side: normal → wide → tele.
      const facingOrder = { environment: 0, user: 1, "": 2 };
      const fa = facingOrder[a.facingMode] ?? 2;
      const fb = facingOrder[b.facingMode] ?? 2;
      if (fa !== fb) return fa - fb;
      const lensOrder = {
        back: 0,
        front: 0,
        ultrawide: 1,
        tele: 2,
        multi: 3,
        unknown: 4,
      };
      const ao = lensOrder[a.lens] ?? 5;
      const bo = lensOrder[b.lens] ?? 5;
      if (ao !== bo) return ao - bo;
      return a.index - b.index;
    });
}

function rawCameraLabel(option) {
  const raw = String(option?.label || "").trim();
  return raw && !/^video\s*input/i.test(raw) ? raw : "";
}

/** Full lens name (tooltip / screen reader): Back normal, Back wide, Front… */
export function webEvalCameraOptionLabel(option, t = (k, d) => d || k) {
  if (!option) return t("liveTrainingEvalCameraBackNormal", "Back normal");
  if (option.lens === "ultrawide") {
    if (option.facingMode === "user") {
      return t("liveTrainingEvalCameraFrontWide", "Front wide");
    }
    return t("liveTrainingEvalCameraBackWide", "Back wide");
  }
  if (option.lens === "tele") {
    return t("liveTrainingEvalCameraTele", "Tele");
  }
  if (option.lens === "multi") {
    return rawCameraLabel(option) || t("liveTrainingEvalCameraAuto", "Auto");
  }
  if (option.lens === "front" || option.facingMode === "user") {
    return t("liveTrainingEvalCameraFront", "Front");
  }
  if (option.lens === "back" || option.facingMode === "environment") {
    return t("liveTrainingEvalCameraBackNormal", "Back normal");
  }
  const raw = rawCameraLabel(option);
  if (raw) return raw;
  const n = Number(option.index) + 1;
  return t("liveTrainingEvalCameraGeneric", `Camera ${n}`);
}

/** Setup chip: Wide, Normal, Front, or Tele — the lens the phone is actually using. */
export function webEvalCameraSetupLabel(option, t = (k, d) => d || k) {
  if (!option) return t("cmCameraNormal", "Normal");
  if (option.lens === "ultrawide") {
    if (option.facingMode === "user") {
      return t("cmCameraFrontWide", "Front wide");
    }
    return t("cmCameraWide", "Wide");
  }
  if (option.lens === "tele") return t("cmCameraTele", "Tele");
  if (option.lens === "multi") return t("cmCameraAuto", "Auto");
  if (option.lens === "front" || option.facingMode === "user") {
    return t("cmCameraFront", "Front");
  }
  if (option.lens === "back" || option.facingMode === "environment") {
    return t("cmCameraNormal", "Normal");
  }
  return webEvalCameraOptionLabel(option, t);
}

export function webEvalCameraButtonLabel(option, options, t = (k, d) => d || k) {
  const list = Array.isArray(options) ? options : [];
  const sameLens = list.filter(
    (item) => item?.lens && item.lens === option?.lens && item.lens !== "unknown"
  );
  if (sameLens.length > 1) {
    const raw = rawCameraLabel(option);
    if (raw) return raw;
  }
  return webEvalCameraOptionLabel(option, t);
}

/** iPhone Camera-style lens chip: 0.5 / 1 / 2 / A / F */
export function webEvalCameraLensMark(option) {
  if (!option) return "1";
  if (option.lens === "ultrawide") {
    return option.zoom > 0 && option.zoom < 1 ? String(option.zoom) : "0.5";
  }
  if (option.lens === "tele") return "2";
  if (option.lens === "multi") return "A";
  if (option.lens === "front" || option.facingMode === "user") return "F";
  return "1";
}

/**
 * Wide lens the open camera reaches by zooming out (Samsung 0.6×, iPhone Dual
 * Wide at 0.5×). Only when the browser does not list that side's ultra-wide as
 * its own camera.
 */
export function webEvalZoomWideLens(selectedOption, options, wideZoom) {
  if (!selectedOption) return null;
  const facingMode = selectedOption.facingMode || "environment";
  const listed = (Array.isArray(options) ? options : []).some(
    (o) => o?.lens === "ultrawide" && (o.facingMode || "environment") === facingMode
  );
  if (listed) return null;
  let zoom = wideZoom > 0 && wideZoom < 1 ? wideZoom : null;
  // Safari often keeps Dual Wide zoom.min at 1 even though 0.5× works.
  if (
    zoom == null &&
    facingMode === "environment" &&
    isWideArrayCameraLabel(selectedOption.label)
  ) {
    zoom = 0.5;
  }
  if (!(zoom > 0 && zoom < 1)) return null;
  return {
    id: `zoom-wide:${selectedOption.id}`,
    deviceId: selectedOption.deviceId,
    facingMode,
    lens: "ultrawide",
    zoom,
    label: "",
    index: selectedOption.index,
  };
}

/** True when the option list includes a selectable wide (0.5×) lens. */
export function webEvalHasWideLens(options) {
  return (Array.isArray(options) ? options : []).some(
    (o) => o?.lens === "ultrawide"
  );
}

export function streamVideoDeviceId(stream) {
  try {
    const id = stream?.getVideoTracks?.()?.[0]?.getSettings?.()?.deviceId;
    return id ? String(id) : "";
  } catch {
    return "";
  }
}

export function isWebEvalCameraSelected(option, selection = {}) {
  if (!option) return false;
  const deviceId = String(selection.deviceId || "");
  if (deviceId && option.deviceId) {
    return option.deviceId === deviceId;
  }
  const facingMode = normalizeWebEvalFacingMode(selection.facingMode);
  return Boolean(option.facingMode) && option.facingMode === facingMode;
}

/** Mic constraints for eval / match / KoT capture (mono, echo-cancelled). */
export const EVAL_VIDEO_AUDIO_CONSTRAINTS = Object.freeze({
  echoCancellation: true,
  noiseSuppression: true,
  channelCount: 1,
});

/**
 * Frame-rate constraint ladder for eval capture.
 * Front cameras often ignore soft `ideal` and settle ~15fps; prefer an exact
 * lock, then min, then ideal/max.
 */
export function evalVideoFrameRateConstraint(
  fps = DEFAULT_WEB_EVAL_FPS,
  mode = "exact"
) {
  const rate = normalizeWebEvalFps(fps);
  if (mode === "min") return { min: rate, ideal: rate };
  if (mode === "ideal") return { ideal: rate, max: rate };
  return { exact: rate };
}

export function evalVideoUserMediaConstraints({
  fps = DEFAULT_WEB_EVAL_FPS,
  deviceId = "",
  facingMode = "environment",
  includeAudio = true,
  deviceIdMode = "exact",
  frameRateMode = "exact",
  relaxResolution = false,
  captureWidth = EVAL_VIDEO_CAPTURE.width,
  captureHeight = EVAL_VIDEO_CAPTURE.height,
} = {}) {
  const rate = normalizeWebEvalFps(fps);
  const video = {
    frameRate: evalVideoFrameRateConstraint(rate, frameRateMode),
  };
  if (!relaxResolution) {
    video.width = { ideal: captureWidth };
    video.height = { ideal: captureHeight };
    video.aspectRatio = {
      ideal: captureWidth / captureHeight,
    };
  }
  const id = String(deviceId || "").trim();
  if (id) {
    video.deviceId = deviceIdMode === "ideal" ? { ideal: id } : { exact: id };
  } else {
    video.facingMode = {
      ideal: normalizeWebEvalFacingMode(facingMode),
    };
  }
  return {
    audio: includeAudio ? { ...EVAL_VIDEO_AUDIO_CONSTRAINTS } : false,
    video,
  };
}

/** Actual track frame rate from getSettings, or null when unknown. */
export function readEvalTrackFrameRate(stream) {
  const track = stream?.getVideoTracks?.()?.[0];
  const rate = Number(track?.getSettings?.()?.frameRate);
  return Number.isFinite(rate) && rate > 0 ? rate : null;
}

function trackMeetsEvalFps(stream, rate) {
  const actual = readEvalTrackFrameRate(stream);
  if (actual == null) return true; // unknown — trust the constraint negotiation
  return actual >= Number(rate) - 1;
}

async function applyEvalFrameRateAttempts(track, rate) {
  const attempts = [
    { frameRate: evalVideoFrameRateConstraint(rate, "exact") },
    { frameRate: evalVideoFrameRateConstraint(rate, "min") },
    { frameRate: evalVideoFrameRateConstraint(rate, "ideal") },
  ];
  for (const constrains of attempts) {
    try {
      await track.applyConstraints(constrains);
      const next = Number(track.getSettings?.()?.frameRate);
      if (Number.isFinite(next) && next >= rate - 1) return true;
    } catch {
      /* try next constraint shape */
    }
  }
  return false;
}

/**
 * Push an open video track toward the target fps (front cams often open at 15).
 * When 60 is requested but unsupported, lock 30 as the worst-case floor.
 */
export async function enforceEvalCaptureFrameRate(
  stream,
  fps = DEFAULT_WEB_EVAL_FPS
) {
  const track = stream?.getVideoTracks?.()?.[0];
  if (!track?.applyConstraints) return stream;
  const ladder = evalCaptureFpsFallbackLadder(fps);
  for (const rate of ladder) {
    if (trackMeetsEvalFps(stream, rate)) return stream;
    const ok = await applyEvalFrameRateAttempts(track, rate);
    if (ok || trackMeetsEvalFps(stream, rate)) return stream;
  }
  return stream;
}

/** True when the track is at least the 30fps worst-case floor (or rate unknown). */
export function evalTrackMeetsMinCaptureFps(stream) {
  const actual = readEvalTrackFrameRate(stream);
  if (actual == null) return true;
  return actual >= MIN_WEB_EVAL_FPS - 1;
}

function stopEvalMediaStream(stream) {
  try {
    stream?.getTracks?.().forEach((t) => t.stop());
  } catch {
    /* ignore */
  }
}

/** Why the last mic request failed (DOMException name), or null once a mic is live. */
let lastEvalMicError = null;
let pendingMicRequest = null;

export function getLastEvalMicError() {
  return lastEvalMicError;
}

function micErrorName(err) {
  return String(err?.name || err?.message || "error").slice(0, 64);
}

async function getEvalUserMediaOnce(media, opts) {
  try {
    const stream = await media.getUserMedia(
      evalVideoUserMediaConstraints({ ...opts, includeAudio: true })
    );
    lastEvalMicError = null;
    return stream;
  } catch (err) {
    let videoOnly;
    try {
      videoOnly = await media.getUserMedia(
        evalVideoUserMediaConstraints({ ...opts, includeAudio: false })
      );
    } catch {
      throw err;
    }
    // Camera works without the mic, so the mic was the problem.
    lastEvalMicError = micErrorName(err);
    return videoOnly;
  }
}

/**
 * Open camera+mic for eval recording. Falls back to video-only if the mic
 * is missing or denied so capture still works.
 * Prefers 60fps, then 30fps worst case — avoids front cameras stuck at ~15fps.
 */
export async function getEvalUserMedia(opts = {}) {
  const media = navigator?.mediaDevices;
  if (!media?.getUserMedia) {
    throw new Error("getUserMedia is not available");
  }
  const preferred = normalizeWebEvalFps(opts.fps, DEFAULT_WEB_EVAL_FPS);
  const ladder = evalCaptureFpsFallbackLadder(preferred);
  let lastErr;
  for (const rate of ladder) {
    const base = { ...opts, fps: rate };
    const attempts = [
      { ...base, frameRateMode: "exact", relaxResolution: false },
      { ...base, frameRateMode: "min", relaxResolution: false },
      { ...base, frameRateMode: "exact", relaxResolution: true },
      { ...base, frameRateMode: "ideal", relaxResolution: false },
    ];
    for (const attempt of attempts) {
      try {
        const stream = await getEvalUserMediaOnce(media, attempt);
        // Prefer the original target (60→30 inside), not only this step's rate.
        await enforceEvalCaptureFrameRate(stream, preferred);
        if (
          trackMeetsEvalFps(stream, rate) ||
          evalTrackMeetsMinCaptureFps(stream)
        ) {
          return stream;
        }
        // Still below 30fps (or unknown failure) — discard and continue ladder.
        stopEvalMediaStream(stream);
      } catch (err) {
        lastErr = err;
      }
    }
  }
  throw lastErr || new Error("getUserMedia failed");
}

/** True when the stream already has a live microphone track. */
export function evalStreamHasLiveAudio(stream) {
  return Boolean(
    stream?.getAudioTracks?.().some((t) => t.readyState === "live")
  );
}

/**
 * Attach a mic track to an existing video-only session stream (e.g. after a
 * camera-only permission prime). No-ops when audio is already present.
 */
export async function ensureEvalStreamHasAudio(stream, { timeoutMs = 0 } = {}) {
  if (!stream) return stream;
  if (evalStreamHasLiveAudio(stream)) {
    enableEvalStreamAudio(stream);
    lastEvalMicError = null;
    return stream;
  }
  const media = navigator?.mediaDevices;
  if (!media?.getUserMedia) {
    lastEvalMicError = "unsupported";
    return stream;
  }
  // One prompt at a time: every recording start would otherwise stack another.
  if (!pendingMicRequest) {
    pendingMicRequest = (async () => {
      try {
        return await media.getUserMedia({
          audio: { ...EVAL_VIDEO_AUDIO_CONSTRAINTS },
          video: false,
        });
      } catch (err) {
        if (err?.name === "NotAllowedError" || err?.name === "SecurityError") {
          throw err;
        }
        // Some Android mics reject the processing constraints; any mic beats none.
        return media.getUserMedia({ audio: true, video: false });
      }
    })().finally(() => {
      pendingMicRequest = null;
    });
  }
  const request = pendingMicRequest;
  let audioOnly = null;
  try {
    audioOnly =
      timeoutMs > 0
        ? await Promise.race([
            request,
            new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs)),
          ])
        : await request;
    if (!audioOnly) {
      // Permission prompt still open: attach the mic whenever it is granted.
      lastEvalMicError = "pending";
      request
        .then((late) => attachEvalAudioTracks(stream, late))
        .catch((err) => {
          lastEvalMicError = micErrorName(err);
        });
      return stream;
    }
  } catch (err) {
    lastEvalMicError = micErrorName(err);
    return stream;
  }
  attachEvalAudioTracks(stream, audioOnly);
  return stream;
}

function attachEvalAudioTracks(stream, audioOnly) {
  audioOnly?.getAudioTracks?.().forEach((track) => {
    try {
      stream.addTrack(track);
    } catch {
      try {
        track.stop();
      } catch {
        /* ignore */
      }
    }
  });
  if (evalStreamHasLiveAudio(stream)) lastEvalMicError = null;
}

/** A parked session stream has its mic disabled; recording it gives silence. */
export function enableEvalStreamAudio(stream) {
  stream?.getAudioTracks?.().forEach((track) => {
    if (track.readyState === "live") track.enabled = true;
  });
  return stream;
}

/**
 * One match upload. A minute is long enough that the phone can send the
 * previous piece while the next one records, without restarting the encoder
 * every few seconds (that restart froze frames on iPhone).
 * At stop, the server merges every piece of this recording into one file.
 */
/** Autoeval windows are ≤60s, so one timeslice delivers the clip in a single blob. */
export const EVAL_RECORD_TIMESLICE_MS = 60_000;

/** Match recording never goes below 30fps, and 60fps is the default. */
export const MATCH_RECORD_FLOOR_FPS = 30;
export const MATCH_RECORD_DEFAULT_FPS = 60;
/** Default / match-side cameras. Scorecam may also use 480. */
export const MATCH_RECORD_MIN_SHORT = 720;
export const MATCH_RECORD_SCORECAM_MIN_SHORT = 480;

export function normalizeMatchRecordFps(raw, fallback = MATCH_RECORD_DEFAULT_FPS) {
  const n = Number(raw);
  if (n === 30 || n === 60) return n;
  return fallback === 30 ? 30 : MATCH_RECORD_DEFAULT_FPS;
}

/** 480 (scorecam), 720, or 1080. */
export function normalizeMatchRecordResolution(
  raw,
  fallback = MATCH_RECORD_MIN_SHORT
) {
  const n = Number(raw);
  if (n === 480 || n === 720 || n === 1080) return n;
  if (fallback === 1080 || fallback === 480) return fallback;
  return MATCH_RECORD_MIN_SHORT;
}

function evenFrameSide(value) {
  const side = Math.max(2, Math.round(Number(value) || 0));
  return side - (side % 2);
}

/** Selected encode box. 480p / 720p / 1080p, following the camera orientation. */
export function matchTargetFrame(resolution, landscape) {
  const res = normalizeMatchRecordResolution(resolution, MATCH_RECORD_MIN_SHORT);
  const longSide = res === 1080 ? 1920 : res === 480 ? 854 : 1280;
  const shortSide = res;
  return landscape
    ? { width: longSide, height: shortSide }
    : { width: shortSide, height: longSide };
}

/**
 * Fit the camera into the selected box. Scale down when the camera is larger.
 * Never scale a smaller frame up (a 640×480 track stays 640×480).
 */
export function matchUploadFrameSize(
  srcW,
  srcH,
  resolution = MATCH_RECORD_MIN_SHORT,
  fps = MATCH_RECORD_DEFAULT_FPS
) {
  const rate = normalizeMatchRecordFps(fps);
  const w0 = Math.max(0, Number(srcW) || 0);
  const h0 = Math.max(0, Number(srcH) || 0);
  const landscape = !w0 || !h0 ? true : w0 >= h0;
  const target = matchTargetFrame(resolution, landscape);
  if (!w0 || !h0) return { ...target, fps: rate };
  const scale = Math.min(1, target.width / w0, target.height / h0);
  return {
    width: evenFrameSide(w0 * scale),
    height: evenFrameSide(h0 * scale),
    fps: rate,
  };
}

/** Ask the camera for the selected size and frame rate, not a smaller one. */
export function matchRecordVideoConstraints({
  landscape = true,
  resolution = MATCH_RECORD_MIN_SHORT,
  fps = MATCH_RECORD_DEFAULT_FPS,
} = {}) {
  const frame = matchTargetFrame(resolution, landscape);
  const rate = normalizeMatchRecordFps(fps);
  return {
    width: { min: frame.width, ideal: frame.width },
    height: { min: frame.height, ideal: frame.height },
    frameRate: { min: rate, ideal: rate },
    aspectRatio: { ideal: landscape ? 16 / 9 : 9 / 16 },
  };
}

export function readMatchTrackCapture(track) {
  const settings = track?.getSettings?.() || {};
  const width = Number(settings.width) || 0;
  const height = Number(settings.height) || 0;
  const frameRate = Number(settings.frameRate);
  return {
    width,
    height,
    frameRate: Number.isFinite(frameRate) && frameRate > 0 ? frameRate : null,
  };
}

/**
 * Compare an open camera with the selection.
 * 60fps may drop to 30. 1080p may drop to 720p.
 * Scorecam may request 480p; match cameras stay ≥720p.
 */
export function matchCaptureAdjustment({
  requestedFps = MATCH_RECORD_DEFAULT_FPS,
  requestedResolution = MATCH_RECORD_MIN_SHORT,
  width = 0,
  height = 0,
  frameRate = null,
} = {}) {
  const fps = normalizeMatchRecordFps(requestedFps);
  const resolution = normalizeMatchRecordResolution(
    requestedResolution,
    MATCH_RECORD_MIN_SHORT
  );
  const minShort =
    resolution === 480
      ? MATCH_RECORD_SCORECAM_MIN_SHORT
      : MATCH_RECORD_MIN_SHORT;
  const short = width && height ? Math.min(width, height) : 0;
  const warnings = [];
  let nextFps = fps;
  let nextResolution = resolution;
  if (frameRate != null && frameRate < fps - 1) {
    if (fps === 60 && frameRate >= MATCH_RECORD_FLOOR_FPS - 1) {
      nextFps = MATCH_RECORD_FLOOR_FPS;
      warnings.push("fps");
    } else {
      return {
        accept: false,
        reject: true,
        reason: "fps",
        fps: nextFps,
        resolution: nextResolution,
        warnings: ["fps"],
      };
    }
  }
  if (short && short < minShort - 8) {
    return {
      accept: false,
      reject: true,
      reason: "resolution",
      fps: nextFps,
      resolution: nextResolution,
      warnings: ["resolution"],
    };
  }
  if (resolution === 1080 && short && short < 1072) {
    nextResolution = MATCH_RECORD_MIN_SHORT;
    warnings.push("resolution");
  }
  const changed = nextFps !== fps || nextResolution !== resolution;
  return {
    accept: !changed,
    reject: false,
    reason: warnings[0] || "",
    fps: nextFps,
    resolution: nextResolution,
    warnings,
  };
}

/**
 * Match camera bitrate by encoded picture size. 720p keeps the iOS figures
 * (Low 0.9 Mbps ≈ 7 MB/min); the 480p scorecam and 1080p scale with the
 * picture so the same tier looks the same at every size.
 */
export const MATCH_RECORD_BITRATES = Object.freeze({
  480: Object.freeze({ low: 450_000, medium: 1_100_000 }),
  720: Object.freeze({ low: 900_000, medium: 2_200_000 }),
  1080: Object.freeze({ low: 1_600_000, medium: 4_000_000 }),
});

/** 480 / 720 / 1080 tier for an encoded frame (short side). */
export function matchRecordTierForFrame(width, height) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  const short = w && h ? Math.min(w, h) : Math.max(w, h);
  if (short >= 1072) return 1080;
  if (short >= MATCH_RECORD_MIN_SHORT - 8) return 720;
  if (short > 0) return 480;
  return MATCH_RECORD_MIN_SHORT;
}

export function matchRecordBitRate(quality, resolution = MATCH_RECORD_MIN_SHORT) {
  const tier =
    MATCH_RECORD_BITRATES[
      normalizeMatchRecordResolution(resolution, MATCH_RECORD_MIN_SHORT)
    ];
  const q = normalizeEvalVideoQuality(quality, "low") === "low" ? "low" : "medium";
  return tier[q];
}

/** Picture + mic per minute, for the settings estimate. */
export function matchRecordMegabytesPerMinute(videoBitRate) {
  const bits = (Number(videoBitRate) || 0) + EVAL_VIDEO_AUDIO_BITRATE;
  return (bits * 60) / 8 / (1024 * 1024);
}

export const MATCH_RECORD_CHUNK_MS = 60_000;
/**
 * Safety cap only. A minute at the rate Chrome actually writes (~5 Mbps)
 * is about 40 MB, so the cap stays above that and does not cut the minute short.
 */
export const MATCH_RECORD_CHUNK_BYTES = 80 * 1024 * 1024;

export function shouldRotateMatchRecordingChunk({
  elapsedMs = 0,
  pendingBytes = 0,
} = {}) {
  const elapsed = Number(elapsedMs) || 0;
  const bytes = Number(pendingBytes) || 0;
  if (elapsed >= MATCH_RECORD_CHUNK_MS) return true;
  if (bytes >= MATCH_RECORD_CHUNK_BYTES) return true;
  return false;
}

/** Software zoom crops through a canvas. Otherwise the camera track is recorded. */
export function matchRecordNeedsCanvas({ zoom = 1, hardwareZoom = false } = {}) {
  const z = Number(zoom) || 1;
  return z > 1.001 && !hardwareZoom;
}

/** Clone live tracks so the recorder can stop without killing the preview. */
export function cloneLiveMediaStream(source) {
  if (!source?.getTracks) return null;
  const out = new MediaStream();
  let video = 0;
  for (const track of source.getTracks()) {
    if (track.readyState !== "live" || typeof track.clone !== "function") continue;
    try {
      const cloned = track.clone();
      out.addTrack(cloned);
      if (track.kind === "video") video += 1;
    } catch {
      /* ignore a track the browser will not clone */
    }
  }
  if (!video) {
    out.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {
        /* ignore */
      }
    });
    return null;
  }
  return out;
}

/** Merge live mic tracks from `source` onto a canvas.captureStream result. */
export function mixEvalAudioIntoCaptureStream(captured, source) {
  if (!captured || !source?.getAudioTracks) return captured;
  source.getAudioTracks().forEach((track) => {
    if (track.readyState !== "live") return;
    const already = captured
      .getAudioTracks?.()
      .some((t) => t.id === track.id || t.label === track.label);
    if (already) return;
    try {
      captured.addTrack(track);
    } catch {
      /* ignore duplicate / ended */
    }
  });
  return captured;
}

export function evalVideoRecorderOptions(quality, mimeType, videoBitRate = null) {
  const videoBitsPerSecond = Math.max(
    250_000,
    Number(videoBitRate) > 0 ? Number(videoBitRate) : evalVideoBitRate(quality)
  );
  const opts = {
    videoBitsPerSecond,
    audioBitsPerSecond: EVAL_VIDEO_AUDIO_BITRATE,
    bitsPerSecond: videoBitsPerSecond + EVAL_VIDEO_AUDIO_BITRATE,
  };
  if (mimeType) opts.mimeType = mimeType;
  return opts;
}

/**
 * Build MediaRecorder with bitrate caps. Never fall back to an uncapped
 * recorder — Chrome already tends to ignore Low and land ~5 Mbps at 720p60.
 * @param {MediaStream} stream
 * @param {string} quality
 * @param {string} [mimeType]
 * @param {typeof MediaRecorder} [MediaRecorderCtor]
 * @param {{ videoBitRate?: number }} [options] overrides the quality tier
 * @returns {MediaRecorder}
 */
export function createEvalMediaRecorder(
  stream,
  quality,
  mimeType,
  MediaRecorderCtor = typeof MediaRecorder !== "undefined" ? MediaRecorder : null,
  { videoBitRate = null } = {}
) {
  if (!MediaRecorderCtor) {
    throw new Error("MediaRecorder is not available");
  }
  const full = evalVideoRecorderOptions(quality, mimeType, videoBitRate);
  const videoBits = full.videoBitsPerSecond;
  const audioBits = full.audioBitsPerSecond;
  /** @type {MediaRecorderOptions[]} */
  const variants = [
    full,
    {
      videoBitsPerSecond: videoBits,
      audioBitsPerSecond: audioBits,
      ...(mimeType ? { mimeType } : {}),
    },
    {
      videoBitsPerSecond: videoBits,
      ...(mimeType ? { mimeType } : {}),
    },
    { videoBitsPerSecond: videoBits },
  ];
  let lastErr = null;
  for (const opts of variants) {
    try {
      return new MediaRecorderCtor(stream, opts);
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr || new Error("MediaRecorder construction failed");
}
